import React, { Component } from 'react'
import moment from 'moment'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from 'antd'

import PageHeaderWrapper from '../../components/PageHeaderWrapper'
import Request from '../../request'

const { RangePicker } = DatePicker
const { Option } = Select
const { TextArea } = Input
const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const defaultDateRange = [
  moment().subtract(6, 'days').startOf('day'),
  moment().endOf('day'),
]

const statusOptions = [
  'completed',
  'busy',
  'failed',
  'in-progress',
  'no-answer',
  'queued',
  'ringing',
]

const directionOptions = ['incoming', 'outbound-api', 'outbound-dial']
const remarkTypeOptions = [
  'Promotional Call',
  'Customer',
  'Sales Agent',
  'Support',
  'Vendor',
  'Other',
]

class ExotelCalls extends Component {
  constructor(props) {
    super(props)

    this.state = {
      calls: [],
      currentPage: 1,
      dateRange: defaultDateRange,
      detailError: null,
      detailLoading: false,
      detailVisible: false,
      direction: undefined,
      error: null,
      fromNumber: '',
      loading: false,
      pageCursors: { 1: {} },
      pageSize: 25,
      pagination: {
        hasNext: false,
        hasPrev: false,
        nextAfter: null,
        pageSize: 25,
        total: 0,
      },
      phoneNumber: '',
      remarkCall: null,
      remarkModalVisible: false,
      remarkSaving: false,
      remarkText: '',
      remarkType: undefined,
      recordingBlobUrl: null,
      recordingError: null,
      recordingLoading: false,
      recordingReady: false,
      selectedCall: null,
      sid: '',
      status: undefined,
      toNumber: '',
    }
  }

  componentDidMount() {
    this.loadCalls({ currentPage: 1, cursor: {} })
  }

  componentWillUnmount() {
    this.revokeRecordingBlobUrl()
  }

  getListParams = (cursor = {}) => {
    const {
      dateRange,
      direction,
      fromNumber,
      pageSize,
      phoneNumber,
      sid,
      status,
      toNumber,
    } = this.state

    const params = {
      details: 'true',
      pageSize,
      sortDirection: 'desc',
      ...cursor,
    }

    if (dateRange && dateRange.length === 2) {
      params.dateFrom = dateRange[0].format(DATE_TIME_FORMAT)
      params.dateTo = dateRange[1].format(DATE_TIME_FORMAT)
    }

    if (fromNumber) {
      params.fromNumber = fromNumber.trim()
    }

    if (toNumber) {
      params.toNumber = toNumber.trim()
    }

    if (phoneNumber) {
      params.phoneNumber = phoneNumber.trim()
    }

    if (sid) {
      params.sid = sid.trim()
    }

    if (status) {
      params.status = status
    }

    if (direction) {
      params.direction = direction
    }

    return params
  }

  isDateRangeValid = () => {
    const { dateRange } = this.state

    if (!dateRange || dateRange.length !== 2) {
      return true
    }

    return dateRange[1].diff(dateRange[0], 'days', true) <= 31
  }

  loadCalls = async ({ currentPage, cursor }) => {
    if (!this.isDateRangeValid()) {
      this.setState({
        error: 'Exotel allows a maximum date range of 31 days per request.',
      })
      return
    }

    this.setState({ error: null, loading: true })

    const response = await Request.getExotelCalls(this.getListParams(cursor))

    if (response.error) {
      this.setState({
        calls: [],
        error: response.message || 'Unable to load Exotel calls.',
        loading: false,
        pagination: {
          hasNext: false,
          hasPrev: currentPage > 1,
          nextAfter: null,
          pageSize: this.state.pageSize,
          total: 0,
        },
      })
      return
    }

    this.setState((prevState) => ({
      calls: response.data || [],
      currentPage,
      error: null,
      loading: false,
      pageCursors: {
        ...prevState.pageCursors,
        [currentPage]: cursor,
      },
      pagination: response.pagination || prevState.pagination,
    }))
  }

  handleSearch = () => {
    this.setState(
      {
        currentPage: 1,
        pageCursors: { 1: {} },
      },
      () => {
        this.loadCalls({ currentPage: 1, cursor: {} })
      }
    )
  }

  handleReset = () => {
    this.setState(
      {
        currentPage: 1,
        dateRange: defaultDateRange,
        direction: undefined,
        error: null,
        fromNumber: '',
        pageCursors: { 1: {} },
        phoneNumber: '',
        sid: '',
        status: undefined,
        toNumber: '',
      },
      () => {
        this.loadCalls({ currentPage: 1, cursor: {} })
      }
    )
  }

  handlePrevious = () => {
    const { currentPage, pageCursors } = this.state

    if (currentPage <= 1) {
      return
    }

    const previousPage = currentPage - 1
    const cursor = pageCursors[previousPage] || {}

    this.loadCalls({ currentPage: previousPage, cursor })
  }

  handleNext = () => {
    const { currentPage, pagination } = this.state

    if (!pagination.nextAfter) {
      return
    }

    this.loadCalls({
      currentPage: currentPage + 1,
      cursor: { after: pagination.nextAfter },
    })
  }

  openCall = async (record) => {
    this.revokeRecordingBlobUrl()
    this.setState({
      detailError: null,
      detailLoading: true,
      detailVisible: true,
      recordingBlobUrl: null,
      recordingError: null,
      recordingLoading: false,
      recordingReady: false,
      selectedCall: record,
    })

    const response = await Request.getExotelCall(record.Sid)

    if (response.error) {
      this.setState({
        detailError: response.message || 'Unable to load call details.',
        detailLoading: false,
      })
      return
    }

    this.setState({
      detailError: null,
      detailLoading: false,
      selectedCall: response.data || record,
    })
  }

  openRemarkModal = (call) => {
    this.setState({
      remarkCall: call,
      remarkModalVisible: true,
      remarkSaving: false,
      remarkText: call.remarkText || '',
      remarkType: call.remarkType || undefined,
    })
  }

  closeRemarkModal = () => {
    this.setState({
      remarkCall: null,
      remarkModalVisible: false,
      remarkSaving: false,
      remarkText: '',
      remarkType: undefined,
    })
  }

  closeCallModal = () => {
    this.revokeRecordingBlobUrl()
    this.setState({
      detailError: null,
      detailLoading: false,
      detailVisible: false,
      recordingBlobUrl: null,
      recordingError: null,
      recordingLoading: false,
      recordingReady: false,
      selectedCall: null,
    })
  }

  revokeRecordingBlobUrl = () => {
    const { recordingBlobUrl } = this.state

    if (recordingBlobUrl) {
      window.URL.revokeObjectURL(recordingBlobUrl)
    }
  }

  hasRecording = (call) => {
    if (!call) {
      return false
    }

    return Boolean(call.hasRecording || call.PreSignedRecordingUrl || call.RecordingUrl)
  }

  loadRecordingBlob = async (call) => {
    if (!call || !this.hasRecording(call) || !call.Sid) {
      this.setState({
        recordingBlobUrl: null,
        recordingError: null,
        recordingLoading: false,
        recordingReady: false,
      })
      return null
    }

    this.setState({
      recordingError: null,
      recordingLoading: true,
    })

    const response = await Request.getExotelRecording(call.Sid)

    if (response.error || !response.data) {
      this.setState({
        recordingBlobUrl: null,
        recordingError: 'Unable to load the recording through the CRM server.',
        recordingLoading: false,
        recordingReady: false,
      })
      return null
    }

    this.revokeRecordingBlobUrl()

    const recordingBlobUrl = window.URL.createObjectURL(response.data)

    this.setState({
      recordingBlobUrl,
      recordingError: null,
      recordingLoading: false,
      recordingReady: true,
    })

    return recordingBlobUrl
  }

  openRecordingBlob = async () => {
    const { recordingBlobUrl, selectedCall } = this.state

    if (recordingBlobUrl) {
      window.open(recordingBlobUrl, '_blank')
      return
    }

    const latestRecordingBlobUrl = await this.loadRecordingBlob(selectedCall)
    if (latestRecordingBlobUrl) {
      window.open(latestRecordingBlobUrl, '_blank')
    }
  }

  saveRemark = async () => {
    const { remarkCall, remarkText, remarkType } = this.state

    if (!remarkCall || !remarkCall.Sid) {
      return
    }

    if (!remarkType && !remarkText.trim()) {
      notification.error({
        message: 'Select a remark type or enter a remark before saving.',
      })
      return
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}')

    this.setState({ remarkSaving: true })

    const response = await Request.saveExotelRemark(remarkCall.Sid, {
      remarkType,
      remark: remarkText,
      user,
    })

    if (response.error) {
      this.setState({ remarkSaving: false })
      notification.error({
        message: response.message || 'Unable to save the remark.',
      })
      return
    }

    const remarkData = response.data || {}

    this.setState((prevState) => ({
      calls: (prevState.calls || []).map((call) => {
        if (call.Sid !== remarkCall.Sid) {
          return call
        }

        return {
          ...call,
          internalRemark: remarkData,
          remarkType: remarkData.remarkType || '',
          remarkText: remarkData.remark || '',
        }
      }),
      remarkCall: null,
      remarkModalVisible: false,
      remarkSaving: false,
      remarkText: '',
      remarkType: undefined,
      selectedCall:
        prevState.selectedCall && prevState.selectedCall.Sid === remarkCall.Sid
          ? {
              ...prevState.selectedCall,
              internalRemark: remarkData,
              remarkType: remarkData.remarkType || '',
              remarkText: remarkData.remark || '',
            }
          : prevState.selectedCall,
    }))

    notification.success({
      message: response.message || 'Remark saved successfully.',
    })
  }

  formatDuration = (value) => {
    const duration = parseInt(value, 10)

    if (Number.isNaN(duration)) {
      return '-'
    }

    const hours = Math.floor(duration / 3600)
    const minutes = Math.floor((duration % 3600) / 60)
    const seconds = duration % 60

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(
        seconds
      ).padStart(2, '0')}`
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
      2,
      '0'
    )}`
  }

  formatDate = (value) => {
    if (!value) {
      return '-'
    }

    return moment(value).format('DD MMM YYYY, hh:mm A')
  }

  getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green'
      case 'in-progress':
      case 'queued':
      case 'ringing':
        return 'blue'
      case 'busy':
      case 'no-answer':
        return 'orange'
      case 'failed':
        return 'red'
      default:
        return 'default'
    }
  }

  formatDetailValue = (value) => {
    if (value === undefined || value === null || value === '') {
      return '-'
    }

    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    return value
  }

  renderDetailGrid = (call) => {
    const items = [
      { label: 'Call SID', value: call.Sid },
      { label: 'From', value: call.From },
      { label: 'To', value: call.To },
      { label: 'Direction', value: call.Direction },
      { label: 'Status', value: call.Status },
      { label: 'Created At', value: this.formatDate(call.DateCreated) },
      { label: 'Started At', value: this.formatDate(call.StartTime) },
      { label: 'Ended At', value: this.formatDate(call.EndTime) },
      { label: 'Duration', value: this.formatDuration(call.Duration) },
      {
        label: 'Conversation Duration',
        value: this.formatDuration(call.ConversationDuration),
      },
      {
        label: 'ExoPhone / Flow',
        value: call.PhoneNumber || call.PhoneNumberSid,
      },
      { label: 'Answered By', value: call.AnsweredBy },
      { label: 'Price', value: call.Price },
      { label: 'Call Type', value: call.CallType },
      { label: 'Forwarded From', value: call.ForwardedFrom },
      { label: 'Caller Name', value: call.CallerName },
    ]

    return (
      <Row gutter={16}>
        {items.map((item) => (
          <Col span={12} key={item.label} style={{ marginBottom: 16 }}>
            <div
              style={{
                color: '#8c8c8c',
                fontSize: 12,
                marginBottom: 4,
                textTransform: 'uppercase',
              }}
            >
              {item.label}
            </div>
            <div style={{ wordBreak: 'break-word' }}>
              {this.formatDetailValue(item.value)}
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  renderCallDetails = (call) => {
    if (!call || !call.Details || typeof call.Details !== 'object') {
      return null
    }

    const detailItems = [
      { label: 'Leg 1 Status', value: call.Details.Leg1Status },
      { label: 'Leg 2 Status', value: call.Details.Leg2Status },
      { label: 'Conference Status', value: call.Details.ConferenceStatus },
      { label: 'Termination Cause', value: call.Details.TerminationCause },
    ].filter((item) => item.value)

    if (!detailItems.length) {
      return null
    }

    return (
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>
          Call Details
        </div>
        <Row gutter={16}>
          {detailItems.map((item) => (
            <Col span={12} key={item.label} style={{ marginBottom: 16 }}>
              <div
                style={{
                  color: '#8c8c8c',
                  fontSize: 12,
                  marginBottom: 4,
                  textTransform: 'uppercase',
                }}
              >
                {item.label}
              </div>
              <div>{this.formatDetailValue(item.value)}</div>
            </Col>
          ))}
        </Row>
      </div>
    )
  }

  render() {
    const {
      calls,
      currentPage,
      dateRange,
      detailError,
      detailLoading,
      detailVisible,
      direction,
      error,
      fromNumber,
      loading,
      pagination,
      phoneNumber,
      remarkCall,
      remarkModalVisible,
      remarkSaving,
      remarkText,
      remarkType,
      recordingBlobUrl,
      recordingError,
      recordingLoading,
      recordingReady,
      selectedCall,
      sid,
      status,
      toNumber,
    } = this.state

    const columns = [
      {
        title: 'From',
        dataIndex: 'From',
        key: 'From',
        render: (value, record) => (
          <div>
            <div>{value || '-'}</div>
            {record.CallerName ? (
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {record.CallerName}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'To',
        dataIndex: 'To',
        key: 'To',
        render: (value) => value || '-',
      },
      {
        title: 'Direction',
        dataIndex: 'Direction',
        key: 'Direction',
        render: (value) => (
          <Tag color="blue">{value ? value.replace(/-/g, ' ') : 'unknown'}</Tag>
        ),
      },
      {
        title: 'ExoPhone / Flow',
        key: 'PhoneNumber',
        render: (value, record) =>
          record.PhoneNumber || record.PhoneNumberSid || '-',
      },
      {
        title: 'Time',
        dataIndex: 'DateCreated',
        key: 'DateCreated',
        render: (value) => this.formatDate(value),
      },
      {
        title: 'Outcome',
        key: 'Status',
        render: (value, record) => (
          <div>
            <Tag color={this.getStatusColor(record.Status)}>
              {record.Status || 'unknown'}
            </Tag>
            {record.AnsweredBy ? (
              <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
                Answered by {record.AnsweredBy}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: 'Call Duration',
        dataIndex: 'Duration',
        key: 'Duration',
        render: (value) => this.formatDuration(value),
      },
      {
        title: 'Recording',
        key: 'hasRecording',
        render: (value, record) =>
          this.hasRecording(record) ? (
            <Tag color="green">Available</Tag>
          ) : (
            <Tag>Not available</Tag>
          ),
      },
      {
        title: 'Remarks',
        key: 'remarks',
        render: (value, record) =>
          record.remarkType || record.remarkText ? (
            <div>
              {record.remarkType ? (
                <Tag color="gold">{record.remarkType}</Tag>
              ) : null}
              {record.remarkText ? (
                <div style={{ color: '#595959', marginTop: 4, maxWidth: 240 }}>
                  {record.remarkText}
                </div>
              ) : null}
            </div>
          ) : (
            <span style={{ color: '#8c8c8c' }}>No remarks</span>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (value, record) => (
          <div>
            <Button onClick={() => this.openRemarkModal(record)} style={{ marginRight: 8 }}>
              Remarks
            </Button>
            <Button type="primary" onClick={() => this.openCall(record)}>
              View
            </Button>
          </div>
        ),
      },
    ]

    return (
      <PageHeaderWrapper
        title={`Exotel Calls: ${pagination.total || calls.length || 0}`}
      >
        <Card bordered={true}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col md={8} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Date Range</div>
              <RangePicker
                allowClear={false}
                format={DATE_TIME_FORMAT}
                onChange={(value) => this.setState({ dateRange: value })}
                showTime={{ format: 'HH:mm:ss' }}
                style={{ width: '100%' }}
                value={dateRange}
              />
            </Col>

            <Col md={4} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Direction</div>
              <Select
                allowClear
                onChange={(value) => this.setState({ direction: value })}
                placeholder="All directions"
                style={{ width: '100%' }}
                value={direction}
              >
                {directionOptions.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col md={4} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Status</div>
              <Select
                allowClear
                onChange={(value) => this.setState({ status: value })}
                placeholder="All statuses"
                style={{ width: '100%' }}
                value={status}
              >
                {statusOptions.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col md={4} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>From</div>
              <Input
                onChange={(event) =>
                  this.setState({ fromNumber: event.target.value })
                }
                onPressEnter={this.handleSearch}
                placeholder="Caller number"
                value={fromNumber}
              />
            </Col>

            <Col md={4} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>To</div>
              <Input
                onChange={(event) =>
                  this.setState({ toNumber: event.target.value })
                }
                onPressEnter={this.handleSearch}
                placeholder="Destination number"
                value={toNumber}
              />
            </Col>

            <Col md={4} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Phone Number</div>
              <Input
                onChange={(event) =>
                  this.setState({ phoneNumber: event.target.value })
                }
                onPressEnter={this.handleSearch}
                placeholder="ExoPhone"
                value={phoneNumber}
              />
            </Col>

            <Col md={8} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Call SID</div>
              <Input
                onChange={(event) => this.setState({ sid: event.target.value })}
                onPressEnter={this.handleSearch}
                placeholder="Exact call SID"
                value={sid}
              />
            </Col>

            <Col md={8} xs={24} style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Actions</div>
              <div>
                <Button
                  loading={loading}
                  onClick={this.handleSearch}
                  type="primary"
                >
                  Search
                </Button>
                <Button
                  onClick={this.handleReset}
                  style={{ marginLeft: 8 }}
                >
                  Reset
                </Button>
              </div>
            </Col>
          </Row>

          {error ? (
            <Alert
              message={error}
              showIcon
              style={{ marginBottom: 16 }}
              type="error"
            />
          ) : null}

          <Alert
            message="This screen reads directly from Exotel. Recordings only appear when Exotel has a recording URL for that call."
            showIcon
            style={{ marginBottom: 16 }}
            type="info"
          />

          <Table
            columns={columns}
            dataSource={calls}
            loading={loading}
            pagination={false}
            rowKey={(record) => record.Sid}
            scroll={{ x: 1200 }}
          />

          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 16,
            }}
          >
            <div>{`Page ${currentPage}${pagination.total ? ` • ${pagination.total} calls` : ''}`}</div>
            <div>
              <Button
                disabled={currentPage <= 1 || loading}
                onClick={this.handlePrevious}
                style={{ marginRight: 8 }}
              >
                Previous
              </Button>
              <Button
                disabled={!pagination.hasNext || loading}
                onClick={this.handleNext}
                type="primary"
              >
                Next
              </Button>
            </div>
          </div>
        </Card>

        <Modal
          destroyOnClose
          footer={[
            <Button key="cancel" onClick={this.closeRemarkModal}>
              Cancel
            </Button>,
            <Button
              key="save"
              loading={remarkSaving}
              onClick={this.saveRemark}
              type="primary"
            >
              Save Remark
            </Button>,
          ]}
          onCancel={this.closeRemarkModal}
          title={remarkCall ? `Remarks for ${remarkCall.Sid}` : 'Call Remarks'}
          visible={remarkModalVisible}
        >
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>
              Classification
            </div>
            <Select
              allowClear
              onChange={(value) => this.setState({ remarkType: value })}
              placeholder="Select a remark type"
              style={{ width: '100%' }}
              value={remarkType}
            >
              {remarkTypeOptions.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 6 }}>
              Remarks
            </div>
            <TextArea
              onChange={(event) => this.setState({ remarkText: event.target.value })}
              placeholder="Add internal notes for this call"
              rows={4}
              value={remarkText}
            />
          </div>
        </Modal>

        <Modal
          destroyOnClose
          footer={[
            <Button key="close" onClick={this.closeCallModal}>
              Close
            </Button>,
          ]}
          onCancel={this.closeCallModal}
          title={selectedCall ? `Exotel Call ${selectedCall.Sid}` : 'Exotel Call'}
          visible={detailVisible}
          width={900}
        >
          {detailLoading ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <Spin size="large" />
            </div>
          ) : null}

          {!detailLoading && detailError ? (
            <Alert message={detailError} showIcon type="error" />
          ) : null}

          {!detailLoading && !detailError && selectedCall ? (
            <div>
              {this.renderDetailGrid(selectedCall)}
              {this.renderCallDetails(selectedCall)}

              {selectedCall.remarkType || selectedCall.remarkText ? (
                <div style={{ marginTop: 24 }}>
                  <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                    Internal Remark
                  </div>
                  {selectedCall.remarkType ? (
                    <Tag color="gold">{selectedCall.remarkType}</Tag>
                  ) : null}
                  {selectedCall.remarkText ? (
                    <div style={{ marginTop: 8 }}>{selectedCall.remarkText}</div>
                  ) : null}
                </div>
              ) : null}

              <div style={{ fontSize: 16, fontWeight: 500, marginTop: 24 }}>
                Recording
              </div>
              <div style={{ marginTop: 12 }}>
                {recordingError ? (
                  <Alert
                    message={recordingError}
                    style={{ marginBottom: 12 }}
                    showIcon
                    type="error"
                  />
                ) : null}

                {!recordingError && !this.hasRecording(selectedCall) ? (
                  <Alert
                    message="No recording URL was returned by Exotel for this call."
                    showIcon
                    type="info"
                  />
                ) : null}

                {!recordingError && this.hasRecording(selectedCall) ? (
                  <Button
                    loading={recordingLoading}
                    onClick={this.openRecordingBlob}
                    type="primary"
                  >
                    {recordingReady || recordingBlobUrl
                      ? 'Open recording in a new tab'
                      : 'Load and open recording'}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </Modal>
      </PageHeaderWrapper>
    )
  }
}

export default ExotelCalls
